import React, { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { Modal, FormField, Button } from '../ui';
import { useConfirm } from '../../hooks/useConfirm';
import { useStore } from '../../store/StoreContext';
import { getTruckConflicts, formatTime12 } from '../../utils/formatters';
import { getLegCrew, getTripTag } from '../../utils/dispatchHelpers';
import { crewOffInfo } from '../../lib/crewOff';

const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400';

export default function AssignModal({ job, onClose, onSave, crew, trucks, clients, jobs: allJobs, mode = 'all' }) {
    const client = clients.find(c => c.id === job.client_id);
    const isLegEdit = job._isLegEdit;
    const legDate = job._legDate;
    const initCrew = isLegEdit ? (getLegCrew(job.trip_tags, legDate) || job.crew_ids || []) : (job.crew_ids || []);
    const initLeader = isLegEdit ? (initCrew[0] || null) : job.leader_id;
    const [selCrew, setSelCrew] = useState(initCrew);
    const [leader, setLeader] = useState(initLeader);
    const [selTrucks, setSelTrucks] = useState(job.truck_ids || []);
    const [saving, setSaving] = useState(false);
    // In-app confirm + toast (QA M2 NEW-9 native-dialog sweep).
    const { confirm, confirmElement } = useConfirm();
    const { show } = useToast();
    // Availability for the OFF-day guard (Susie 2026-08-06) — pulled from
    // the store rather than threading a prop through every caller.
    const { availability } = useStore();
    const toggleC = id => setSelCrew(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
    const toggleT = id => setSelTrucks(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

    // Surface any double-bookings in the CURRENT selection (vs. just
    // disabling un-selected booked trucks like the picker does
    // already). This catches cases where the conflict pre-exists from
    // an earlier assignment / a direct insert / a drag-and-drop swap.
    // Each entry: { truck, conflictingJobs }.
    // Same-lead rule (2026-08-07): conflicts are computed against the lead
    // CURRENTLY selected in this modal — a truck already on this lead's
    // other jobs today is his truck for the day, not a double-booking.
    const effLeader = leader || selCrew[0] || null;
    const selectedTruckConflicts = (!isLegEdit && allJobs)
        ? selTrucks
            .map(tid => {
                const truck = trucks.find(t => t.id === tid);
                const conflicts = getTruckConflicts(tid, job.move_date, job.duration, job.time, job.id, allJobs, effLeader);
                if (!truck || !conflicts) return null;
                return { truck, conflicts };
            })
            .filter(Boolean)
        : [];
    const hasTruckConflicts = selectedTruckConflicts.length > 0;

    const handleSave = async () => {
        // Soft-block: if the current truck selection would create a
        // double-booking, force the dispatcher to confirm before save.
        // This catches the "I know I'm overriding the warning" case
        // explicitly rather than letting it slip through silently.
        if (hasTruckConflicts) {
            const names = selectedTruckConflicts.map(c => c.truck.name).join(', ');
            const ok = await confirm({
                title: `Save ${client?.name || 'this job'} with ${names} double-booked?`,
                body: `${names} ${selectedTruckConflicts.length === 1 ? 'is' : 'are'} already on another crew's job this day. Both jobs keep the truck — save only if the crews are really handing it off.`,
                confirmLabel: 'Save anyway',
                cancelLabel: 'Go back',
            });
            if (!ok) return;
        }
        // ── OFF-day guard (Susie 2026-08-06) ──
        // Selecting someone who's off for this job's date (right-click
        // OFF, a specific day-off request, or their recurring weekly
        // day) requires an explicit confirm before saving.
        {
            const guardDate = isLegEdit ? legDate : job.move_date;
            const offPicked = guardDate
                ? selCrew
                    .map(id => {
                        const m = crew.find(c => c.id === id);
                        const info = crewOffInfo(m, guardDate, availability);
                        return info.off ? { name: m?.name || 'Unknown', info } : null;
                    })
                    .filter(Boolean)
                : [];
            if (offPicked.length > 0) {
                const lines = offPicked.map(p => `${p.name} (${p.info.source === 'recurring' ? 'weekly day off' : (p.info.note || 'off')})`).join(', ');
                const ok = await confirm({
                    title: `Assign ${offPicked.map(p => p.name).join(', ')} to ${client?.name || 'this job'} on a day off?`,
                    body: `${lines} — marked off for this date. The day off stays on the board; the assignment is saved on top of it.`,
                    confirmLabel: 'Assign anyway',
                    cancelLabel: 'Go back',
                });
                if (!ok) return;
            }
        }
        setSaving(true);
        try {
            if (isLegEdit) {
                const newTags = { ...(job.trip_tags || {}) };
                Object.entries(newTags).forEach(([d, v]) => {
                    if (d === job.move_date) return;
                    const tag = typeof v === 'string' ? v : (v?.tag || 'Drive');
                    newTags[d] = { tag, leg_crew_ids: selCrew };
                });
                const { _isLegEdit, _legDate, _isSpanDay, _spanDayNum, _assignMode, ...cleanJob } = job;
                await onSave({ ...cleanJob, trip_tags: newTags });
            } else {
                const { _isSpanDay, _spanDayNum, _assignMode: _m, ...cleanJob2 } = job;
                // Crew edits never DOWNGRADE a live job (QA M5 BUG-3): saving an
                // assignment on a dispatched/in_progress job used to silently
                // demote it to 'assigned' — which also disarmed the crew-swap
                // notice after its first firing. Live statuses stick.
                const keepLive = ['dispatched', 'in_progress'].includes(job.status);
                await onSave({ ...cleanJob2, crew_ids: selCrew, leader_id: leader || selCrew[0] || null, truck_ids: selTrucks, status: selCrew.length > 0 ? (keepLive ? job.status : 'assigned') : 'unassigned' });
            }
            onClose();
        } catch(e) { show('Save failed: ' + (e.message || 'Unknown error'), 'error'); }
        finally { setSaving(false); }
    };

    const tagForLeg = isLegEdit ? getTripTag(job.trip_tags, legDate) : null;

    return (
        <Modal title={isLegEdit ? `Trip Crew: ${client?.name || 'Job'} — ${tagForLeg || 'Travel'}` : mode === 'truck' ? `Truck Assignment: ${client?.name || 'Job'}` : mode === 'crew' ? `Crew Assignment: ${client?.name || 'Job'}` : `Assign: ${client?.name || 'Job'}`} onClose={onClose} width="max-w-xl">
            <div className="mb-3 text-sm text-slate-600">
                <div><strong>Type:</strong> {job.type} &bull; <strong>Date:</strong> {isLegEdit ? legDate : job.move_date} &bull; <strong>Time:</strong> {job.time}</div>
                <div><strong>From:</strong> {job.from_address}</div>
                <div><strong>To:</strong> {job.to_address}</div>
                {isLegEdit && <div className="mt-1 text-amber-600 font-medium">Editing trip crew — changes apply to all travel days</div>}
                {!isLegEdit && (() => {
                    const crewNeeded = client?.crew_size_needed || 0;
                    const trucksNeededIds = (() => { try { return JSON.parse(client?.trucks_needed || '[]'); } catch { return []; } })();
                    const trucksNeededNames = trucksNeededIds.map(id => trucks.find(t => t.id === id)?.name).filter(Boolean);
                    return (crewNeeded > 0 || trucksNeededNames.length > 0) ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                            <span className="font-bold text-slate-500 uppercase">Needs:</span>
                            {crewNeeded > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold">{crewNeeded} crew</span>}
                            {trucksNeededNames.map((name, i) => <span key={i} className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">{name}</span>)}
                        </div>
                    ) : null;
                })()}
            </div>
            {mode !== 'truck' && (
            <FormField label={isLegEdit ? "Trip Crew" : "Crew"}>
                <div className="flex flex-wrap gap-2">
                    {crew.filter(c => c.active !== false).map(c => {
                        const sel = selCrew.includes(c.id);
                        const isLoadCrew = isLegEdit && (job.crew_ids || []).includes(c.id);
                        return (
                            <div key={c.id} className="flex items-center gap-1">
                                <button type="button" onClick={() => toggleC(c.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 ${sel ? 'text-white' : 'bg-white text-slate-600 border-slate-300'}`} style={sel ? { backgroundColor: c.display_color, borderColor: c.display_color } : {}}>{c.name}{isLoadCrew ? ' (Load)' : ''}</button>
                                {sel && !isLegEdit && <button type="button" onClick={() => setLeader(c.id)} className={`text-xs px-1.5 py-0.5 rounded ${leader === c.id ? 'bg-yellow-400 text-yellow-900 font-bold' : 'bg-slate-200 text-slate-500'}`}>L</button>}
                            </div>
                        );
                    })}
                </div>
            </FormField>
            )}
            {!isLegEdit && mode !== 'crew' && (
                <FormField label="Trucks">
                    <div className="flex flex-wrap gap-2">{trucks.map(t => {
                        const sel = selTrucks.includes(t.id);
                        // Same-lead rule (2026-08-07): pass the selected lead
                        // so this lead's other jobs today don't flag their own
                        // truck. Cross-crew overlaps stay flagged — but as a
                        // clickable warn (amber), not a hard block: mid-day
                        // truck handoffs between crews are real, and the save
                        // still requires an explicit confirm.
                        const conflict = allJobs ? getTruckConflicts(t.id, job.move_date, job.duration, job.time, job.id, allJobs, effLeader) : null;
                        const isConflict = !!conflict;
                        const selWithConflict = sel && isConflict;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => toggleT(t.id)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                                    selWithConflict
                                        ? 'bg-red-600 text-white border-red-600 ring-2 ring-red-200'
                                        : sel
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : isConflict
                                                ? 'bg-amber-50 text-amber-700 border-amber-300'
                                                : 'bg-white text-slate-600 border-slate-300'
                                }`}
                                title={isConflict ? `On another crew's job: ${conflict.map(j => formatTime12(j.time) + ' (' + (j.duration||4) + 'h)').join(', ')} — tap to share anyway (you'll confirm at save)` : ''}
                            >
                                {t.name}
                                {selWithConflict ? ' ⚠' : isConflict ? ' (in use)' : ''}
                            </button>
                        );
                    })}</div>
                </FormField>
            )}
            {/* Inline conflict banner — shown when one or more selected
                trucks would create a double-booking. Made deliberately
                loud so the dispatcher sees it before tapping Save. */}
            {hasTruckConflicts && (
                <div role="alert" className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="text-xs font-bold text-red-600 mb-1 flex items-center gap-1.5">
                        <span className="text-base">⚠</span> Truck double-booking
                    </div>
                    {selectedTruckConflicts.map(({ truck, conflicts }) => {
                        const overlaps = conflicts.map(j => {
                            const otherClient = clients.find(c => c.id === j.client_id);
                            return `${otherClient?.name || 'another job'} at ${formatTime12(j.time)} (${j.duration || 4}h)`;
                        }).join('; ');
                        return (
                            <div key={truck.id} className="text-xs text-red-600 ml-6">
                                <strong>{truck.name}</strong> already booked: {overlaps}
                            </div>
                        );
                    })}
                    <div className="text-[10px] text-red-600 mt-1.5 ml-6">
                        Saving will require explicit confirmation.
                    </div>
                </div>
            )}
            {!isLegEdit && (() => {
                const crewNeeded = client?.crew_size_needed || 0;
                const trucksNeededIds = (() => { try { return JSON.parse(client?.trucks_needed || '[]'); } catch { return []; } })();
                const warnings = [];
                if (crewNeeded > 0 && selCrew.length < crewNeeded) {
                    warnings.push(`Crew size: ${selCrew.length} assigned, ${crewNeeded} needed`);
                }
                if (trucksNeededIds.length > 0) {
                    const missingTrucks = trucksNeededIds.filter(id => !selTrucks.includes(id));
                    if (missingTrucks.length > 0) {
                        const names = missingTrucks.map(id => trucks.find(t => t.id === id)?.name || '?').join(', ');
                        warnings.push(`Missing truck(s): ${names}`);
                    }
                }
                return warnings.length > 0 ? (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                        <div className="text-xs font-bold text-amber-800 mb-1 flex items-center gap-1.5">
                            <span className="text-base">⚠️</span> Pre-allocation requirements not met
                        </div>
                        {warnings.map((w, i) => <div key={i} className="text-xs text-amber-700 ml-6">&bull; {w}</div>)}
                        <div className="text-[10px] text-amber-600 mt-1.5 ml-6">You can still save, but this may affect the move.</div>
                    </div>
                ) : (crewNeeded > 0 || trucksNeededIds.length > 0) ? (
                    <div className="mt-3 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                        <div className="text-xs font-medium text-green-700 flex items-center gap-1.5">
                            <span className="text-sm">✓</span> Pre-allocation requirements met
                        </div>
                    </div>
                ) : null;
            })()}
            <div className="flex justify-end gap-3 mt-4">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={saving}>{isLegEdit ? 'Save Trip Crew' : 'Save Assignment'}</Button>
            </div>
            {confirmElement}
        </Modal>
    );
}
